import { useAtomValue } from 'jotai'

import { cn, formatDurationHuman } from '@/utils'
import { addMilliseconds } from 'date-fns'
import _clamp from 'lodash/clamp'
import _round from 'lodash/round'

import { eventAtom } from '@/store/event.store'
import { momentAtom } from '@/store/moment.store'
import { rundownAtom } from '@/store/rundown.store'
import { timestampsAtom } from '@/store/timestamps.store'

import { useRundownOutputOptions } from './useRundownOutputOptions.ts'
import * as OUTPUT_TYPES from './rundown-output.const.ts'

import CueItemElapsed from '@/components/rundown/CueItemElapsed'
import { CurrentTimeOfDay } from '@/components/rundown/current-time-of-day'
import { RundownOutputTextFitWrapper } from '@/features/rundown-output/RundownOutputTextFitWrapper'

const TickState = {
  FUTURE: 'future',
  NOW: 'now',
  PAST: 'past',
  OVERTIME: 'overtime',
}

export function RundownOutputHeader() {
  const rundown = useAtomValue(rundownAtom)
  const event = useAtomValue(eventAtom)
  const moment = useAtomValue(momentAtom)
  const timestamps = useAtomValue(timestampsAtom)

  const { getOption } = useRundownOutputOptions()

  const optionLogo = getOption(OUTPUT_TYPES.LOGO)

  if (!rundown) return null

  const percent = 1 - moment?.left / (moment?.total || 1)
  const progressPct = _clamp(_round(percent * 100, 2), 0, 100)
  const overtime = moment?.left <= 0

  if (!timestamps) return null

  const actualEnd = addMilliseconds(
    timestamps.actual.start,
    timestamps.actual.duration,
  )
  const originalEnd = addMilliseconds(
    timestamps.original.start,
    timestamps.original.duration,
  )

  const diff = originalEnd.getTime() - actualEnd.getTime()

  return (
    <div
      className={cn(
        'flex',
        getOption(OUTPUT_TYPES.HEADER_POSITION) === OUTPUT_TYPES.TOP
          ? 'flex-col'
          : 'flex-col-reverse',
      )}
      style={{ height: '10vh' }}
    >
      <div className="flex justify-end items-center gap-8 p-3 pb-0">
        {/* Rundown title */}
        {getOption(OUTPUT_TYPES.RUNDOWN_TITLE) === OUTPUT_TYPES.OFF ? (
          ''
        ) : (
          <RundownOutputTextFitWrapper height="3vh" className="font-semibold">
            <div className="whitespace-nowrap text-ellipsis overflow-hidden">
              {rundown?.name}
            </div>
          </RundownOutputTextFitWrapper>
        )}

        <span className="flex-1"></span>

        {/* Logo */}
        {optionLogo === OUTPUT_TYPES.OFF ? (
          ''
        ) : (
          <div
            className="text-center"
            style={{ height: 'auto', width: 'auto' }}
          >
            {optionLogo?.includes('http') ? (
              <img className="max-h-[3vh] w-max" src={optionLogo} />
            ) : (
              <img
                className="max-h-[3vh] aspect-auto my-auto"
                src={rundown.logo || event?.logo || '/img/logo.svg'}
              />
            )}
          </div>
        )}

        {/* Current cue timer */}
        {getOption(OUTPUT_TYPES.CUE_TIMER) === OUTPUT_TYPES.OFF ? (
          ''
        ) : (
          <RundownOutputTextFitWrapper height="3vh">
            <div className="whitespace-nowrap font-mono font-semibold">
              <CueItemElapsed
                moment={moment}
                className="bg-transparent!"
                overtimeClassName=""
              />
            </div>
          </RundownOutputTextFitWrapper>
        )}

        {/* Time of day */}
        {getOption(OUTPUT_TYPES.TIME_OF_DAY) === OUTPUT_TYPES.OFF ? (
          ''
        ) : (
          <RundownOutputTextFitWrapper height="3vh">
            <div className="whitespace-nowrap font-mono">
              <CurrentTimeOfDay
                timezone={rundown.timezone ?? undefined}
                showTimezone
                todDisplayFormat={rundown.settings?.todDisplayFormat}
              />
            </div>
          </RundownOutputTextFitWrapper>
        )}

        {/* Over/Under */}
        {getOption(OUTPUT_TYPES.OVER_UNDER) === OUTPUT_TYPES.OFF ? (
          ''
        ) : (
          <RundownOutputTextFitWrapper height="3vh">
            <div className="font-mono">
              <span className="text-sm leading-6">
                {diff >= 1000 ? (
                  <p className="text-green-500">
                    <span className="font-mono">
                      {formatDurationHuman(diff)}
                    </span>{' '}
                    early
                  </p>
                ) : (
                  ''
                )}
                {diff < 1000 && diff > -1000 ? '--' : ''}
                {diff <= -1000 ? (
                  <p className="text-red-500">
                    <span className="font-mono">
                      {formatDurationHuman(diff)}
                    </span>{' '}
                    late
                  </p>
                ) : (
                  ''
                )}
              </span>
            </div>
          </RundownOutputTextFitWrapper>
        )}
      </div>

      <div className="px-3 flex justify-end gap-4">
        {/* Progress bar */}
        {getOption(OUTPUT_TYPES.PROGRESS_BAR) === OUTPUT_TYPES.OFF ? (
          ''
        ) : (
          <div className="grow h-[4vh] flex items-center">
            <div className="flex w-full bg-gray-900 rounded-[0.4vh]">
              {/* PROGRESS */}
              <div className={'relative grow h-[2vh]'}>
                {/* PROGRESS BACKGROUND */}
                <div className="w-full h-full rounded-[0.4vh] bg-linear-to-r from-blue-500 to-red-600"></div>

                {/* PROGRESS DARK OVERLAY */}
                <div
                  style={{ width: `${progressPct}%` }}
                  className={cn(
                    'absolute top-0 left-0 bottom-0 rounded-[0.4vh] bg-black/70 transition-all',
                    progressPct < 100 ? ' rounded-r-none!' : '',
                  )}
                ></div>
              </div>
            </div>
          </div>
        )}
        {/* Last five seconds */}
        {getOption(OUTPUT_TYPES.LAST_FIVE_SECONDS) === OUTPUT_TYPES.OFF ? (
          ''
        ) : (
          <div className="h-[4vh] flex items-center gap-2">
            {[5000, 4000, 3000, 2000, 1000].map((ms, i) => {
              let state = TickState.FUTURE
              if (overtime) state = TickState.OVERTIME
              else if (moment?.left <= ms - 1000) state = TickState.PAST
              else if (moment?.left <= ms) state = TickState.NOW
              const flash = overtime && Math.ceil(moment?.left / 1000) % 2 === 0
              return (
                <div
                  key={'5-sec-' + i}
                  className={cn(
                    'pointer-events-none h-[2.4vh]',
                    'aspect-square rounded-[0.7vh] border-[0.1vh] transition-all hover:border-white',
                    state === TickState.FUTURE
                      ? 'border-gray-900 bg-red-600'
                      : '',
                    state === TickState.NOW ? 'border-white bg-red-600' : '',
                    state === TickState.PAST
                      ? 'border-gray-900 bg-red-600/30'
                      : '',
                    state === TickState.OVERTIME && !flash
                      ? 'border-gray-900 bg-red-600/30'
                      : '',
                    state === TickState.OVERTIME && flash
                      ? 'border-white bg-red-600'
                      : '',
                    state,
                  )}
                  style={flash ? { boxShadow: '#b91c1b 0 0 8px' } : undefined}
                ></div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
